import React, { useEffect, useMemo, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import { useParams } from "react-router-dom";
import { Container } from "reactstrap";
import { getInspectionList } from "../../helpers/backend_helper";
import "./InspectionCycleReport.scss";

export const reportDefinitions = {
  "inspection-summary": {
    title: "Inspection Summary Report",
    description: "Report to view the complete inspection details.",
    icon: "bx-line-chart",
    subject: "Inspections",
    metric: "Total Inspections",
    count: "24",
  },
  "document-access": {
    title: "Document Access Report",
    description: "Report on document access across inspection cycles.",
    icon: "bx-folder",
    subject: "Documents",
    metric: "Documents Accessed",
    count: "186",
  },
  "risk-assessment": {
    title: "Risk Assessment Report",
    description: "Summary of risks identified across inspections.",
    icon: "bx-shield-quarter",
    subject: "Risk Items",
    metric: "Open Risks",
    count: "18",
  },
  "inspection-timeliness": {
    title: "Inspection Timeliness Report",
    description: "Report on inspection cycle timelines and due dates.",
    icon: "bx-time-five",
    subject: "Inspection Cycles",
    metric: "On-Time Cycles",
    count: "19",
  },
  engagement: {
    title: "Engagement Report",
    description: "Report on engagements based on status.",
    icon: "bx-file-find",
    subject: "Engagements",
    metric: "Total Engagements",
    count: "36",
  },
  "search-task": {
    title: "Search Task Report",
    description: "Report on task status.",
    icon: "bx-search-alt",
    subject: "Tasks",
    metric: "Open Tasks",
    count: "42",
  },
  "engagement-team": {
    title: "Engagement Team Report",
    description: "Report on engagement team assignments and roles.",
    icon: "bx-group",
    subject: "Team Assignments",
    metric: "Active Members",
    count: "18",
  },
  "upcoming-due-dates": {
    title: "Upcoming Due Dates Report",
    description: "Report on upcoming tasks and engagement due dates.",
    icon: "bx-calendar-event",
    subject: "Due Items",
    metric: "Due This Month",
    count: "12",
  },
  "engagement-progress": {
    title: "Engagement Progress Report",
    description: "Overall progress of engagements across stages.",
    icon: "bx-check-circle",
    subject: "Engagements",
    metric: "Average Progress",
    count: "68%",
  },
  "overdue-tasks": {
    title: "Overdue Tasks Report",
    description: "Report on overdue tasks and delayed activities.",
    icon: "bx-error-circle",
    subject: "Overdue Tasks",
    metric: "Needs Attention",
    count: "7",
  },
  "files-detail": {
    title: "Files Detail Report",
    description: "Report to view the complete file details.",
    icon: "bx-file",
    subject: "Files",
    metric: "Managed Files",
    count: "284",
  },
  "access-permissions": {
    title: "Access & Permissions Report",
    description: "Report on user access and permissions.",
    icon: "bx-shield",
    subject: "Access Rules",
    metric: "Active Permissions",
    count: "64",
  },
  "user-activity": {
    title: "User Activity Report",
    description: "Report on user activities and actions performed.",
    icon: "bx-user",
    subject: "Activities",
    metric: "Recent Activities",
    count: "128",
  },
  "login-audit": {
    title: "Login Audit Report",
    description: "Report on user logins and security events.",
    icon: "bx-lock-alt",
    subject: "Audit Events",
    metric: "Successful Logins",
    count: "94",
  },
  findings: {
    title: "Findings Report",
    description: "Summary of findings raised across engagements.",
    icon: "bx-flag",
    subject: "Findings",
    metric: "Open Findings",
    count: "15",
  },
  queries: {
    title: "Queries Report",
    description: "Report on all queries raised during reviews.",
    icon: "bx-question-mark",
    subject: "Queries",
    metric: "Open Queries",
    count: "9",
  },
  conclusion: {
    title: "Conclusion Report",
    description: "Report on final conclusions of engagement reviews.",
    icon: "bx-task",
    subject: "Conclusions",
    metric: "Finalised Reviews",
    count: "21",
  },
  "review-outcome": {
    title: "Review Outcome Report",
    description: "Summary of review outcomes across engagements.",
    icon: "bx-bookmark",
    subject: "Review Outcomes",
    metric: "Completed Outcomes",
    count: "27",
  },
  "partner-review": {
    title: "Partner Review Report",
    description: "Report on partner review and sign-off status.",
    icon: "bx-bar-chart-alt-2",
    subject: "Partner Reviews",
    metric: "Awaiting Sign-off",
    count: "4",
  },
  "data-export": {
    title: "Data Export Report",
    description: "Report on data exports and downloads.",
    icon: "bx-download",
    subject: "Exports",
    metric: "Exports This Month",
    count: "16",
  },
  "compliance-dashboard": {
    title: "Compliance Dashboard Report",
    description: "Summary of compliance activity and status.",
    icon: "bx-chart",
    subject: "Compliance Areas",
    metric: "Compliant Areas",
    count: "22",
  },
  "review-portfolio": {
    title: "Review Portfolio Report",
    description: "Portfolio view of review activity across the firm.",
    icon: "bx-briefcase",
    subject: "Portfolio Items",
    metric: "Active Reviews",
    count: "31",
  },
  "custom-report-builder": {
    title: "Custom Report Builder",
    description: "Create and run a saved report using your preferred fields.",
    icon: "bx-slider-alt",
    subject: "Saved Reports",
    metric: "Saved Reports",
    count: "6",
  },
};

const statuses = [
  "In Progress",
  "Completed",
  "In Progress",
  "Pending Review",
  "Completed",
];
const completionReportIds = new Set(["conclusion", "review-outcome"]);

const formatReportDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const reviewProgress = (review) =>
  Math.max(0, Math.min(100, Number(review.percentage) || 0));

const ReportDetail = () => {
  const { reportId } = useParams();
  const definition =
    reportDefinitions[reportId] || reportDefinitions.engagement;
  const isCompletionReport = completionReportIds.has(reportId);
  const [reviews, setReviews] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [status, setStatus] = useState("All Statuses");
  const [owner, setOwner] = useState("All Owners");
  const [resultSearch, setResultSearch] = useState("");

  useEffect(() => {
    if (!isCompletionReport) return;
    getInspectionList()
      .then((response) =>
        setReviews(
          response?.status && Array.isArray(response.data) ? response.data : []
        )
      )
      .catch(() => setReviews([]));
  }, [isCompletionReport]);

  const rows = useMemo(() => {
    if (isCompletionReport) {
      return reviews
        .filter(
          (review) =>
            /completed/i.test(review.inspectionStatus?.name || "") ||
            reviewProgress(review) === 100
        )
        .map((review) => ({
          name: `${
            reportId === "conclusion"
              ? "Checklist completion"
              : "Review completion"
          } — ${
            review.inspectionNo ||
            review.engagementDetail?.name ||
            "Engagement review"
          }`,
          owner:
            review.assignedUser?.fullName ||
            review.ownerUser?.fullName ||
            "Unassigned",
          status: review.inspectionStatus?.name || "Completed",
          dueDate: formatReportDate(review.dueDate),
          progress: reviewProgress(review),
          updated: formatReportDate(
            review.completedOn || review.updatedOn || review.createdOn
          ),
        }));
    }
    return Array.from({ length: 5 }, (_, index) => ({
      name: `${definition.subject.replace(/s$/, "")} ${String(
        index + 1
      ).padStart(2, "0")}`,
      owner: [
        "ABC Firm Admin",
        "Taylor Brooks",
        "Morgan Lee",
        "Alex Rivera",
        "Casey Morgan",
      ][index],
      status: statuses[index],
      dueDate: [
        "30 Sep 2026",
        "15 Oct 2026",
        "31 Oct 2026",
        "14 Nov 2026",
        "30 Nov 2026",
      ][index],
      progress: [68, 100, 45, 30, 100][index],
      updated: [
        "18 Aug 2026",
        "17 Aug 2026",
        "15 Aug 2026",
        "12 Aug 2026",
        "10 Aug 2026",
      ][index],
    }));
  }, [definition.subject, isCompletionReport, reportId, reviews]);

  const completedRows = rows.filter(
    (row) => /completed/i.test(row.status) || row.progress === 100
  );
  const reportMetric = isCompletionReport
    ? completedRows.length
    : definition.count;

  const filteredRows = rows.filter((row) => {
    const matchesStatus = status === "All Statuses" || row.status === status;
    const matchesOwner = owner === "All Owners" || row.owner === owner;
    const term = resultSearch.trim().toLowerCase();
    return (
      matchesStatus &&
      matchesOwner &&
      (!term || Object.values(row).join(" ").toLowerCase().includes(term))
    );
  });

  const exportReport = () => {
    const csv = [
      [
        definition.subject,
        "Owner",
        "Status",
        "Due Date",
        "Progress",
        "Last Updated",
      ],
      ...filteredRows.map((row) => [
        row.name,
        row.owner,
        row.status,
        row.dueDate,
        `${row.progress}%`,
        row.updated,
      ]),
    ]
      .map((line) => line.map((value) => `"${value}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `${reportId}-report.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="inspection-report page-content piers-page">
      <Container fluid>
        <section className="inspection-report__workspace">
          <header className="inspection-report__heading piers-page-header">
            <div>
              <h1 className="piers-page-title">
                {definition.title}
                <button
                  type="button"
                  aria-label={`Add ${definition.title} to favourites`}
                >
                  <i className="bx bx-star" />
                </button>
              </h1>
              <p>{definition.description}</p>
            </div>
            <div className="inspection-report__actions">
              <button
                className="inspection-report__export-main"
                type="button"
                onClick={exportReport}
              >
                <i className="bx bx-download" /> Export
              </button>
            </div>
          </header>
          <section className="inspection-report__filters">
            <header>
              <b>
                <i className="bx bx-slider-alt" /> Search Parameters
              </b>
              <button
                type="button"
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {filtersOpen ? "Hide Filters" : "Show Filters"}{" "}
                <i className={`bx bx-chevron-${filtersOpen ? "up" : "down"}`} />
              </button>
            </header>
            {filtersOpen && (
              <div className="inspection-report__filter-content">
                <div className="inspection-report__filter-grid">
                  <label className="inspection-report__field">
                    <span>Status</span>
                    <select
                      value={status}
                      onChange={(event) => setStatus(event.target.value)}
                    >
                      <option>All Statuses</option>
                      {[...new Set(statuses)].map((item) => (
                        <option key={item}>{item}</option>
                      ))}
                    </select>
                  </label>
                  <label className="inspection-report__field">
                    <span>Owner</span>
                    <select
                      value={owner}
                      onChange={(event) => setOwner(event.target.value)}
                    >
                      <option>All Owners</option>
                      {[...new Set(rows.map((row) => row.owner))].map(
                        (item) => (
                          <option key={item}>{item}</option>
                        )
                      )}
                    </select>
                  </label>
                  <label className="inspection-report__field">
                    <span>Period From</span>
                    <div className="inspection-report__date">
                      <input placeholder="Select date" />
                      <i className="bx bx-calendar" />
                    </div>
                  </label>
                  <label className="inspection-report__field">
                    <span>Period To</span>
                    <div className="inspection-report__date">
                      <input placeholder="Select date" />
                      <i className="bx bx-calendar" />
                    </div>
                  </label>
                  <label className="inspection-report__field">
                    <span>Firm</span>
                    <select>
                      <option>ABC Firm</option>
                    </select>
                  </label>
                </div>
              </div>
            )}
            <div className="inspection-report__applied">
              <b>Applied Filters:</b>
              {status !== "All Statuses" && (
                <button type="button" onClick={() => setStatus("All Statuses")}>
                  Status: {status} <i className="bx bx-x" />
                </button>
              )}
              {owner !== "All Owners" && (
                <button type="button" onClick={() => setOwner("All Owners")}>
                  Owner: {owner} <i className="bx bx-x" />
                </button>
              )}
            </div>
          </section>
          <SummaryCards
            items={[
              [
                definition.icon,
                "blue",
                definition.metric,
                reportMetric,
                "Current reporting period",
              ],
              [
                "bx-time-five",
                "green",
                "In Progress",
                rows.filter((row) => /progress/i.test(row.status)).length,
                "Active records",
              ],
              [
                "bx-check-circle",
                "purple",
                "Completed",
                completedRows.length,
                "Closed records",
              ],
              [
                "bx-calendar-event",
                "orange",
                "Due This Month",
                0,
                "Scheduled due dates",
              ],
              [
                "bx-error-circle",
                "rose",
                "Needs Attention",
                0,
                "Items requiring review",
              ],
            ]}
            ariaLabel="Report summary"
          />
          <section className="inspection-report__results">
            <header>
              <h2>
                Report Results <span>({filteredRows.length})</span>
              </h2>
              <div>
                <label>
                  <i className="bx bx-search" />
                  <input
                    value={resultSearch}
                    onChange={(event) => setResultSearch(event.target.value)}
                    placeholder="Search in results..."
                  />
                </label>
              </div>
            </header>
            <div className="inspection-report__table-wrap">
              <table>
                <thead>
                  <tr>
                    {[
                      definition.subject,
                      "Owner",
                      "Status",
                      "Due Date",
                      "Progress",
                      "Last Updated",
                    ].map((column) => (
                      <th key={column}>
                        {column} <i className="bx bx-sort-alt-2" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.name}>
                      <td>{row.name}</td>
                      <td>{row.owner}</td>
                      <td>
                        <span
                          className={`inspection-report__status is-${row.status
                            .toLowerCase()
                            .replace(" ", "-")}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td>{row.dueDate}</td>
                      <td>
                        <span className="inspection-report__progress">
                          <i>
                            <em style={{ width: `${row.progress}%` }} />
                          </i>
                          {row.progress}%
                        </span>
                      </td>
                      <td>{row.updated}</td>
                    </tr>
                  ))}
                  {!filteredRows.length && (
                    <tr>
                      <td className="inspection-report__no-results" colSpan="6">
                        No records match your search parameters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <footer>
              <b>
                1–{filteredRows.length} of {rows.length}
              </b>
            </footer>
          </section>
        </section>
      </Container>
    </main>
  );
};

export default ReportDetail;
